import { useState, useCallback, useEffect, useMemo } from 'react';
import { BackendApiService, GoogleSheetsConfig } from '../services/backendApi';
import { KPIMetrics, Employee, BacklogData } from '../types';

export const useGoogleSheets = (config: GoogleSheetsConfig, selectedTeams?: string[], selectedHorizontalTeams?: string[], selectedMonth?: string) => {
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetrics | null>(null);
  const [teams, setTeams] = useState<string[]>([]);
  const [design, setDesign] = useState<string[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesTable, setEmployeesTable] = useState<any[]>([]);
  const [backlogData, setBacklogData] = useState<BacklogData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  const googleSheets = useMemo(() => new BackendApiService(), []);

  // Check backend health
  useEffect(() => {
    const checkHealth = async () => {
      setBackendStatus('checking');
      try {
        const isHealthy = await googleSheets.checkHealth();
        setBackendStatus(isHealthy ? 'online' : 'offline');
      } catch (error) {
        setBackendStatus('offline');
      }
    };

    checkHealth();
  }, [googleSheets]);

  // Refresh all data
  const refreshData = useCallback(async () => {
    if (backendStatus !== 'online') {
      setError('Backend service is offline');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await googleSheets.fetchAllData();
      
      // Don't set KPI metrics here - they will be fetched by App.tsx with correct month parameter
      // setKpiMetrics(data.kpiMetrics);
      setTeams(data.teams || []);
      setDesign(data.design || []);
      setEmployees(data.employees);
      setBacklogData(data.backlogData);
      
      // Employees table is fetched separately when filters change
      // This avoids conflicts and ensures it's always up-to-date with current filters
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [googleSheets, backendStatus, selectedTeams, selectedHorizontalTeams]);

  // Initial data fetch
  useEffect(() => {
    if (backendStatus === 'online') {
      refreshData();
    }
  }, [refreshData, backendStatus]);

  // Update employees table when filters change or backend comes online
  // This ensures employees table is always in sync with current filter selections
  useEffect(() => {
    if (backendStatus === 'online') {
      const updateEmployeesTable = async () => {
        try {
          setIsLoading(true);
          const teamParam = selectedTeams && selectedTeams.length > 0 ? selectedTeams.join(',') : undefined;
          const horizontalTeamParam = selectedHorizontalTeams && selectedHorizontalTeams.length > 0 ? selectedHorizontalTeams.join(',') : undefined;
          // Send month parameter even if "all" is selected, so backend can handle it appropriately
          const monthParam = selectedMonth;
          
          console.log('[useGoogleSheets] Updating employees table with filters:', { 
            selectedTeams, 
            selectedHorizontalTeams,
            selectedMonth,
            teamParam, 
            horizontalTeamParam,
            monthParam
          });
          
          const employeesTableData = await googleSheets.fetchEmployeesTable(teamParam, horizontalTeamParam, monthParam);
          console.log('[useGoogleSheets] Updated employees table:', employeesTableData.length, 'employees');
          console.log('[useGoogleSheets] Sample employees:', employeesTableData.slice(0, 3).map(emp => ({ 
            name: emp.employee, 
            team: emp.team 
          })));
          
          setEmployeesTable(employeesTableData);
          setError(null);
        } catch (err) {
          console.error('[useGoogleSheets] Failed to update employees table:', err);
          setError('Failed to update employees table');
        } finally {
          setIsLoading(false);
        }
      };
      
      updateEmployeesTable();
    } else {
      // Clear employees table when backend is offline
      setEmployeesTable([]);
    }
  }, [selectedTeams, selectedHorizontalTeams, selectedMonth, backendStatus, googleSheets]);

  return {
    // Data
    kpiMetrics,
    teams,
    employees,
    employeesTable,
    backlogData,
    design,
    
    // Loading states
    isLoading,
    
    // Error states
    error,
    
    // Backend status
    backendStatus,
    
    // Functions
    refreshData,
    setKpiMetrics,
  };
};
